/**
 * @fileoverview useWebAuthnSignIn – React hook for passkey-based sign-in
 *
 * Wraps `performPasskeySignIn` from WebAuthnSignInService with React state
 * management.  After a successful sign-in the Supabase client will have an
 * active session; the caller can optionally use `wrappedKey` to initialise the
 * encryption session in the same render cycle.
 *
 * @module hooks/useWebAuthnSignIn
 */

import { useState, useCallback } from 'react';
import { createLogger } from '@xanectech/lib';
import { performPasskeySignIn } from '../services/WebAuthnSignInService';
import {
  importMasterKey,
  initializeSessionWithMasterKey,
} from '@xanectech/encryption';
import { useAuth } from '../contexts/AuthContext';

const logger = createLogger('useWebAuthnSignIn');

export interface WebAuthnSignInResult {
  success: boolean;
  error?: string;
}

export function useWebAuthnSignIn() {
  const { user } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Initiate a passkey sign-in for the given email.
   *
   * Triggers the browser's biometric / passkey prompt.  If the server also
   * returns wrapped encryption-key material, the encryption session is
   * initialised automatically once the user's Supabase `user` object becomes
   * available.
   */
  const signIn = useCallback(
    async (email: string): Promise<WebAuthnSignInResult> => {
      setIsSigningIn(true);
      setError(null);

      try {
        const result = await performPasskeySignIn(email);

        if (!result.success) {
          setError(result.error ?? 'Passkey sign-in failed');
          return { success: false, error: result.error };
        }

        // Optionally initialise the encryption session if wrapped key was
        // returned in the same round-trip and we already have a user ID.
        if (result.wrappedKey && user?.id) {
          try {
            const unwrappedKeyString = result.wrappedKey.encryptedMasterKey;
            const decodeBase64ToBytes = (value: string): Uint8Array => {
              try {
                const normalized = value
                  .replace(/-/g, '+')
                  .replace(/_/g, '/');
                const padded = normalized.padEnd(
                  normalized.length +
                    ((4 - (normalized.length % 4)) % 4),
                  '=',
                );
                return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
              } catch {
                return new TextEncoder().encode(value);
              }
            };
            const masterKeyMaterial = decodeBase64ToBytes(
              String(unwrappedKeyString),
            );
            const masterKey = await importMasterKey(masterKeyMaterial);
            await initializeSessionWithMasterKey(user.id, masterKey);
            logger.info(
              'Encryption session initialised during passkey sign-in',
            );
          } catch (encErr) {
            // Non-fatal – encryption unlock can be done separately
            logger.warn(
              'Failed to auto-initialise encryption session after sign-in',
              { error: encErr instanceof Error ? encErr.message : String(encErr) },
            );
          }
        }

        logger.info('Passkey sign-in completed');
        return { success: true };
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Passkey sign-in failed';
        logger.error('useWebAuthnSignIn unexpected error', err as Error);
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setIsSigningIn(false);
      }
    },
    [user],
  );

  const clearError = useCallback(() => setError(null), []);

  return { signIn, isSigningIn, error, clearError };
}
